// export const BASE_URL = 'http://192.168.0.175/campus_marketplace/backend/api';
// export const IMAGE_BASE_URL = 'http://192.168.0.175/campus_marketplace/backend';

export const BASE_URL = 'https://api.ecomart.ng';
export const IMAGE_BASE_URL = 'https://api.ecomart.ng';

// Enhanced getAuthHeaders with token validation
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  
  // Check if token exists and is not expired
  if (!token || isTokenExpired(token)) {
    handleTokenExpiry();
    return { 'Content-Type': 'application/json' };
  }
  
  return { 
    'Authorization': `Bearer ${token}`, 
    'Content-Type': 'application/json' 
  };
};

const getAuthHeadersForFormData = () => {
  const token = localStorage.getItem('adminToken');
  
  // Check if token exists and is not expired
  if (!token || isTokenExpired(token)) {
    handleTokenExpiry();
    return {};
  }
  
  return { 
    'Authorization': `Bearer ${token}`
    // Note: No Content-Type header for FormData - browser will set it
  };
};

// Check if token is expired
const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true;
  }
};


// Handle token expiry - redirect to login
const handleTokenExpiry = () => {
  console.log('Token expired or invalid, redirecting to login...');
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminData');
  // Use window.location for immediate redirect
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Enhanced API call wrapper with token expiry handling
const apiCall = async (url, options = {}) => {
  try {
    console.log(`🔄 API Call: ${url}`, options);
    
    // Use appropriate headers based on content type
    const headers = options.body instanceof FormData 
      ? getAuthHeadersForFormData()
      : getAuthHeaders();
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    console.log(`📡 Response Status: ${response.status} for ${url}`);

    // Check for 401 Unauthorized
    if (response.status === 401) {
      handleTokenExpiry();
      throw new Error('Authentication failed');
    }

    // Clone the response for reading body once
    const responseClone = response.clone();
    
    // Read response text once and store it
    const responseText = await response.text();
    
    // Handle 204 No Content
    if (response.status === 204) {
      console.log(`✅ API Success (204 No Content): ${url}`);
      return { success: true };
    }

    if (!response.ok) {
      // Try to parse error response as JSON
      try {
        const errorData = JSON.parse(responseText);
        console.error(`❌ API Error ${response.status}:`, errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      } catch (jsonError) {
        // If not JSON, use text
        console.error(`❌ API Error ${response.status}:`, responseText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    // Parse successful response from stored text
    try {
      const data = JSON.parse(responseText);
      console.log(`✅ API Success: ${url}`, data);
      return data;
    } catch (parseError) {
      console.error(`❌ Failed to parse response as JSON:`, responseText);
      throw new Error('Invalid JSON response from server');
    }
    
  } catch (error) {
    if (error.message === 'Authentication failed') {
      throw error;
    }
    console.error('❌ API call error:', error);
    throw error;
  }
};

// ==================== AUTH API ====================
export const login = async (credentials) => {
  const response = await fetch(`${BASE_URL}/auth/admin-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });
  
  console.log('🔐 Login Response Status:', response.status);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Admin login failed: ${errorText}`);
  }
  
  const data = await response.json();
  console.log('🔐 Login Response:', data);
  return data;
};

// ==================== DASHBOARD API ====================
export const getDashboardStats = async () => {
  return apiCall(`${BASE_URL}/admin/dashboard/stats`);
};

export const getDashboardCharts = async () => {
  return apiCall(`${BASE_URL}/admin/dashboard/charts`);
};

export const getPendingActions = async () => {
  return apiCall(`${BASE_URL}/admin/dashboard/pending-actions`);
};

// ==================== USER MANAGEMENT API ====================
export const getUsers = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/users?${queryString}`);
};

export const getUserDetail = async (userId) => {
  return apiCall(`${BASE_URL}/admin/users/${userId}`);
};

export const suspendUser = async (userId, reason = 'Violation of terms of service') => {
  return apiCall(`${BASE_URL}/admin/users/${userId}/suspend`, {
    method: 'PUT',
    body: JSON.stringify({ reason })
  });
};

export const activateUser = async (userId) => {
  return apiCall(`${BASE_URL}/admin/users/${userId}/activate`, {
    method: 'PUT'
  });
};

// ==================== SELLER MANAGEMENT API ====================
export const getSellers = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/sellers?${queryString}`);
};

export const approveSeller = async (sellerId) => {
  return apiCall(`${BASE_URL}/admin/sellers/${sellerId}/approve`, {
    method: 'PUT'
  });
};

export const rejectSeller = async (sellerId, reason = 'Application does not meet requirements') => {
  return apiCall(`${BASE_URL}/admin/sellers/${sellerId}/reject`, {
    method: 'PUT',
    body: JSON.stringify({ reason })
  });
};

export const suspendSeller = async (sellerId, reason = 'Violation of marketplace policies') => {
  return apiCall(`${BASE_URL}/admin/sellers/${sellerId}/suspend`, {
    method: 'PUT',
    body: JSON.stringify({ reason })
  });
};

// ==================== LISTING MANAGEMENT API ====================
export const getListings = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/listings?${queryString}`);
};

export const getListingDetail = async (listingId) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}`);
};

export const approveListing = async (listingId) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}/approve`, {
    method: 'PUT'
  });
};

export const rejectListing = async (listingId, adminNotes) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}/reject`, {
    method: 'PUT',
    body: JSON.stringify({ admin_notes: adminNotes })
  });
};

export const featureListing = async (listingId, durationDays = 7) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}/feature`, {
    method: 'PUT',
    body: JSON.stringify({ duration_days: durationDays })
  });
};

export const unfeatureListing = async (listingId) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}/unfeature`, {
    method: 'PUT'
  });
};

export const deleteListing = async (listingId, adminNotes) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}`, {
    method: 'POST',
    body: JSON.stringify({ admin_notes: adminNotes })
  });
};

export const updateListingModerationStatus = async (listingId, status, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}/moderation-status`, {
    method: 'POST',
    body: JSON.stringify({ status, admin_notes: adminNotes })
  });
};

export const getListingPerformance = async (listingId) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}/performance`);
};

export const updateListingSpecializedData = async (listingId, specializedData) => {
  return apiCall(`${BASE_URL}/admin/listings/${listingId}/specialized-data`, {
    method: 'PUT',
    body: JSON.stringify({ specialized_data: specializedData })
  });
};

export const bulkModerateListings = async (listingIds, action, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/listings/bulk-moderate`, {
    method: 'POST',
    body: JSON.stringify({ 
      listing_ids: listingIds, 
      action, 
      admin_notes: adminNotes 
    })
  });
};


export const exportListings = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  
  return fetch(`${BASE_URL}/admin/listings/export?${queryString}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Accept': 'text/csv'
    }
  })
  .then(response => {
    if (!response.ok) {
      return response.json().then(err => { throw new Error(err.message || 'Export failed'); });
    }
    
    return response.blob().then(blob => {
      // Get filename from header or use default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'listings_export.csv';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      return {
        blob,
        filename,
        url: URL.createObjectURL(blob)
      };
    });
  });
};

// Optional: Helper function to download the exported file
export const downloadExportedListings = (exportData) => {
  const link = document.createElement('a');
  link.href = exportData.url;
  link.download = exportData.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(exportData.url);
};

// ==================== ORDER MANAGEMENT API ====================
export const getOrders = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/orders?${queryString}`);
};

export const getOrderDetail = async (orderId) => {
  return apiCall(`${BASE_URL}/admin/orders/${orderId}`);
};

export const updateOrderStatus = async (orderId, status, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/orders/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, admin_notes: adminNotes })
  });
};

export const addOrderNotes = async (orderId, notes) => {
  return apiCall(`${BASE_URL}/admin/orders/${orderId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ notes })
  });
};

export const getOrderStatistics = async () => {
  return apiCall(`${BASE_URL}/admin/orders/statistics`);
};


// ==================== ESCROW MANAGEMENT API ====================

export const getEscrows = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/escrows?${queryString}`);
};

export const getEscrowDetail = async (escrowId) => {
  return apiCall(`${BASE_URL}/admin/escrows/${escrowId}`);
};

export const releaseEscrowFunds = async (escrowId, releaseAmount = null, adminNotes = '', force = false) => {
  return apiCall(`${BASE_URL}/admin/escrows/${escrowId}/release`, {
    method: 'POST',
    body: JSON.stringify({ 
      release_amount: releaseAmount, 
      admin_notes: adminNotes,
      force: force 
    })
  });
};

export const refundEscrowFunds = async (escrowId, refundAmount = null, adminNotes = '', force = false) => {
  return apiCall(`${BASE_URL}/admin/escrows/${escrowId}/refund`, {
    method: 'POST',
    body: JSON.stringify({ 
      refund_amount: refundAmount, 
      admin_notes: adminNotes,
      force: force 
    })
  });
};

export const resolveEscrowDispute = async (escrowId, resolution, refundAmount = 0, releaseAmount = 0, resolutionNotes = '') => {
  return apiCall(`${BASE_URL}/admin/escrows/${escrowId}/resolve-dispute`, {
    method: 'POST',
    body: JSON.stringify({ 
      resolution,
      refund_amount: refundAmount,
      release_amount: releaseAmount,
      resolution_notes: resolutionNotes
    })
  });
};

export const addEscrowNotes = async (escrowId, notes) => {
  return apiCall(`${BASE_URL}/admin/escrows/${escrowId}/notes`, {
    method: 'POST',
    body: JSON.stringify({ notes })
  });
};

export const updateEscrowStatus = async (escrowId, status, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/escrows/${escrowId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ 
      status, 
      admin_notes: adminNotes 
    })
  });
};

export const adjustEscrowFees = async (escrowId, platformFee = null, paystackFee = null, buyerShare = null, sellerShare = null, sellerReceives = null, adminNotes = '') => {
  const body = { admin_notes: adminNotes };
  if (platformFee !== null) body.platform_fee = platformFee;
  if (paystackFee !== null) body.paystack_fee = paystackFee;
  if (buyerShare !== null) body.buyer_share = buyerShare;
  if (sellerShare !== null) body.seller_share = sellerShare;
  if (sellerReceives !== null) body.seller_receives = sellerReceives;
  
  return apiCall(`${BASE_URL}/admin/escrows/${escrowId}/adjust-fees`, {
    method: 'PUT',
    body: JSON.stringify(body)
  });
};

export const getEscrowStatistics = async () => {
  return apiCall(`${BASE_URL}/admin/escrows/statistics`);
};

export const exportEscrows = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/escrows/export?${queryString}`);
};

export const formatEscrowAmount = (amount) => {
  // Handle null, undefined, or invalid amounts
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '₦0.00';
  }
  
  // Ensure it's a number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '₦0.00';
  }
  
  return `₦${numAmount.toLocaleString('en-NG', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
};

export const getEscrowStatusOptions = () => {
  return [
    { id: 'pending', label: 'Pending', color: 'warning' },
    { id: 'held', label: 'Held', color: 'info' },
    { id: 'released', label: 'Released', color: 'success' },
    { id: 'refunded', label: 'Refunded', color: 'danger' },
    { id: 'disputed', label: 'Disputed', color: 'error' },
    { id: 'cancelled', label: 'Cancelled', color: 'secondary' },
    { id: 'expired', label: 'Expired', color: 'default' }
  ];
};

// ==================== FINANCE MANAGEMENT API ====================

// ==================== TRANSACTION API ====================
export const getTransactions = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/transactions?${queryString}`);
};

export const getTransactionDetail = async (transactionId) => {
  return apiCall(`${BASE_URL}/admin/finance/transactions/${transactionId}`);
};

export const updateTransactionStatus = async (transactionId, status, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/finance/transactions/${transactionId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, admin_notes: adminNotes })
  });
};

export const getTransactionStats = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/transactions/stats?${queryString}`);
};

// ==================== PAYMENT API ====================
export const getPayments = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/payments?${queryString}`);
};

export const getPaymentDetail = async (paymentId) => {
  return apiCall(`${BASE_URL}/admin/finance/payments/${paymentId}`);
};

export const updatePaymentStatus = async (paymentId, status, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/finance/payments/${paymentId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, admin_notes: adminNotes })
  });
};

export const getPaymentStats = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/payments/stats?${queryString}`);
};

export const resendPaymentNotification = async (paymentId) => {
  return apiCall(`${BASE_URL}/admin/finance/payments/${paymentId}/resend-notification`, {
    method: 'POST'
  });
};

// ==================== PAYMENT VERIFICATION API ====================
export const verifyPayment = async (paymentId) => {
  return apiCall(`${BASE_URL}/admin/finance/payments/${paymentId}/verify`, {
    method: 'POST'
  });
};

// ==================== WALLET API ====================
export const getWallets = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/wallets?${queryString}`);
};

export const getWalletDetail = async (walletId) => {
  return apiCall(`${BASE_URL}/admin/finance/wallets/${walletId}`);
};

export const updateWalletStatus = async (walletId, status, reason = '') => {
  return apiCall(`${BASE_URL}/admin/finance/wallets/${walletId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, reason })
  });
};

export const manualWalletAdjustment = async (walletId, amount, type, reason = 'Manual adjustment by admin', description = 'Manual balance adjustment') => {
  return apiCall(`${BASE_URL}/admin/finance/wallets/${walletId}/adjust`, {
    method: 'POST',
    body: JSON.stringify({ 
      amount, 
      type, 
      reason, 
      description 
    })
  });
};

export const getWalletStats = async () => {
  return apiCall(`${BASE_URL}/admin/finance/wallets/stats`);
};

// ==================== FEE CONFIGURATION API ====================
export const getAllFeeSettings = async () => {
  return apiCall(`${BASE_URL}/admin/finance/fee-settings`);
};

export const updatePlatformFee = async (feeId, data) => {
  return apiCall(`${BASE_URL}/admin/finance/fee-settings/platform/${feeId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const updateProcessorFee = async (processorId, data) => {
  return apiCall(`${BASE_URL}/admin/finance/fee-settings/processor/${processorId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const updateTieredFee = async (tierId, data) => {
  return apiCall(`${BASE_URL}/admin/finance/fee-settings/tiered/${tierId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const updateFeeSplit = async (splitId, data) => {
  return apiCall(`${BASE_URL}/admin/finance/fee-settings/split/${splitId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const toggleFeeActive = async (table, id, isActive) => {
  return apiCall(`${BASE_URL}/admin/finance/fee-settings/toggle/${table}/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ is_active: isActive })
  });
};

// ==================== FEE ANALYTICS API ====================
export const getFeeAnalytics = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/fee-analytics?${queryString}`);
};

export const getFeeTransactions = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/fee-transactions?${queryString}`);
};

export const getFeeForecast = async (period = 'monthly') => {
  return apiCall(`${BASE_URL}/admin/finance/fee-forecast?period=${period}`);
};

export const exportFeeReport = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/export-fee-report?${queryString}`);
};

// ==================== FINANCIAL REPORTS API ====================
export const getFinancialReports = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/reports?${queryString}`);
};

export const exportFinancialReport = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/finance/reports/export?${queryString}`);
};

// ==================== BOOST MANAGEMENT API ====================
export const getBoostPackages = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/boost/packages?${queryString}`);
};

export const createBoostPackage = async (packageData) => {
  return apiCall(`${BASE_URL}/admin/boost/packages`, {
    method: 'POST',
    body: JSON.stringify(packageData)
  });
};

export const updateBoostPackage = async (packageId, packageData) => {
  return apiCall(`${BASE_URL}/admin/boost/packages/${packageId}`, {
    method: 'PUT',
    body: JSON.stringify(packageData)
  });
};

export const deleteBoostPackage = async (packageId) => {
  return apiCall(`${BASE_URL}/admin/boost/packages/${packageId}`, {
    method: 'DELETE'
  });
};

export const getBoostOrders = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/boost/orders?${queryString}`);
};

export const getBoostOrderDetail = async (orderId) => {
  return apiCall(`${BASE_URL}/admin/boost/orders/${orderId}`);
};

export const updateBoostOrderStatus = async (orderId, status, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/boost/orders/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, admin_notes: adminNotes })
  });
};

export const expireBoost = async (boostOrderId) => {
  return apiCall(`${BASE_URL}/admin/boost/orders/${boostOrderId}/expire`, {
    method: 'PUT'
  });
};

export const getBoostAnalytics = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/boost/analytics?${queryString}`);
};

export const processExpiredBoosts = async () => {
  return apiCall(`${BASE_URL}/admin/boost/process-expired`, {
    method: 'POST'
  });
};

export const getBoostListings = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/boost/listings?${queryString}`);
};


// ==================== SYSTEM MANAGEMENT API ====================

// Admin Management
export const getAdmins = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/system/admins?${queryString}`, {
    method: 'GET'
  });
};

export const createAdmin = async (adminData) => {
  return apiCall(`${BASE_URL}/admin/system/admins`, {
    method: 'POST',
    body: JSON.stringify(adminData)
  });
};

export const updateAdmin = async (adminId, adminData) => {
  return apiCall(`${BASE_URL}/admin/system/admins/${adminId}`, {
    method: 'PUT',
    body: JSON.stringify(adminData)
  });
};

export const deleteAdmin = async (adminId) => {
  return apiCall(`${BASE_URL}/admin/system/admins/${adminId}`, {
    method: 'DELETE'
  });
};

// Admin Activities
export const getAdminActivities = async (adminId = null, params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = adminId 
    ? `${BASE_URL}/admin/system/activities/${adminId}?${queryString}`
    : `${BASE_URL}/admin/system/activities?${queryString}`;
  return apiCall(url, { method: 'GET' });
};

// System Settings
export const getSystemSettings = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/system/settings?${queryString}`, {
    method: 'GET'
  });
};

export const updateSystemSettings = async (settings) => {
  return apiCall(`${BASE_URL}/admin/system/settings`, {
    method: 'PUT',
    body: JSON.stringify(settings)
  });
};

export const initializeDefaultSettings = async () => {
  return apiCall(`${BASE_URL}/admin/system/settings/init`, {
    method: 'POST'
  });
};

// Logo & Media Management
export const uploadLogo = async (file, type = 'logo', metadata = {}) => {
  const formData = new FormData();
  formData.append('logo', file);
  formData.append('type', type);
  formData.append('title', metadata.title || 'Platform Logo');
  formData.append('alt_text', metadata.altText || 'Platform Logo');
  
  // Use apiCall with FormData
  return apiCall(`${BASE_URL}/admin/system/upload-logo`, {
    method: 'POST',
    body: formData
    // Note: Don't set Content-Type header here, browser will do it
  });
};

export const getMediaList = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/system/media?${queryString}`, {
    method: 'GET'
  });
};

export const deleteMedia = async (mediaId) => {
  return apiCall(`${BASE_URL}/admin/system/media/${mediaId}`, {
    method: 'DELETE'
  });
};

export const setActiveMedia = async (mediaId) => {
  return apiCall(`${BASE_URL}/admin/system/media/${mediaId}/active`, {
    method: 'PUT',
    body: JSON.stringify({}) // Empty body for PUT
  });
};

// System Health & Maintenance
export const getSystemHealth = async () => {
  return apiCall(`${BASE_URL}/admin/system/health`, { method: 'GET' });
};

export const runMaintenanceTask = async (task) => {
  return apiCall(`${BASE_URL}/admin/system/maintenance`, {
    method: 'POST',
    body: JSON.stringify({ task })
  });
};

// ==================== CATEGORY MANAGEMENT API ====================
export const getCategories = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/categories?${queryString}`);
};

export const getCategoryTree = async () => {
  return apiCall(`${BASE_URL}/admin/categories/tree`);
};

export const getCategoryDetail = async (categoryId) => {
  return apiCall(`${BASE_URL}/admin/categories/${categoryId}`);
};

export const createCategory = async (categoryData) => {
  return apiCall(`${BASE_URL}/admin/categories`, {
    method: 'POST',
    body: JSON.stringify(categoryData)
  });
};

export const updateCategory = async (categoryId, categoryData) => {
  return apiCall(`${BASE_URL}/admin/categories/${categoryId}`, {
    method: 'PUT',
    body: JSON.stringify(categoryData)
  });
};

export const deleteCategory = async (categoryId) => {
  return apiCall(`${BASE_URL}/admin/categories/${categoryId}`, {
    method: 'DELETE'
  });
};

export const bulkUpdateCategories = async (categoryIds, updates) => {
  return apiCall(`${BASE_URL}/admin/categories/bulk-update`, {
    method: 'POST',
    body: JSON.stringify({ category_ids: categoryIds, updates })
  });
};

export const getCategoryAnalytics = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/categories/analytics?${queryString}`);
};

export const getLocationStats = async () => {
  return apiCall(`${BASE_URL}/admin/categories/location-stats`);
};

export const getAvailableIcons = async () => {
  return apiCall(`${BASE_URL}/admin/categories/icons`);
};

export const exportCategories = async (format = 'json') => {
  return apiCall(`${BASE_URL}/admin/categories/export?format=${format}`);
};

// ==================== REVIEW MODERATION API ====================
export const getPendingReviews = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/reviews/pending?${queryString}`);
};

export const getReviewDetail = async (reviewId) => {
  return apiCall(`${BASE_URL}/admin/reviews/${reviewId}`);
};

export const approveReview = async (reviewId, adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/reviews/${reviewId}/approve`, {
    method: 'PUT',
    body: JSON.stringify({ admin_notes: adminNotes })
  });
};

export const rejectReview = async (reviewId, reason = 'Violation of review guidelines', adminNotes = '') => {
  return apiCall(`${BASE_URL}/admin/reviews/${reviewId}/reject`, {
    method: 'PUT',
    body: JSON.stringify({ 
      rejection_reason: reason, 
      admin_notes: adminNotes 
    })
  });
};

export const getReviewStats = async () => {
  return apiCall(`${BASE_URL}/admin/reviews/stats`);
};

// ==================== DISPUTE MANAGEMENT API ====================
// Get all disputes - returns { disputes: [], pagination: {} }
export const getDisputes = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/disputes?${queryString}`);
};

// Get dispute detail - returns { dispute: {}, evidence: [], messages: [] }
export const getDisputeDetail = async (disputeId) => {
  return apiCall(`${BASE_URL}/admin/disputes/${disputeId}`);
};

// Update dispute status
export const updateDisputeStatus = async (disputeId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/disputes/${disputeId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Resolve dispute
export const resolveDispute = async (disputeId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/disputes/${disputeId}/resolve`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Add admin evidence
export const addAdminEvidence = async (disputeId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/disputes/${disputeId}/evidence`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

// Escalate dispute
export const escalateDispute = async (disputeId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/disputes/${disputeId}/escalate`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Close dispute
export const closeDispute = async (disputeId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/disputes/${disputeId}/close`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Get dispute statistics - returns { stats: {}, categories: [], resolutions: [], recent_disputes: [] }
export const getDisputeStats = async () => {
  return apiCall(`${BASE_URL}/admin/disputes/stats`);
};


// ==================== SUPPORT MANAGEMENT API ====================
// Get all tickets (admin access) - returns { tickets: [], pagination: {} }
export const getTickets = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/support/tickets?${queryString}`);
};

// Get single ticket detail - returns { ticket: {}, attachments: [], dispute: {}, order: {} }
export const getTicketDetail = async (ticketId) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}`);
};

// Assign ticket to admin
export const assignTicket = async (ticketId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}/assign`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Add response to ticket
export const addTicketResponse = async (ticketId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}/respond`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

// Update ticket status
export const updateTicketStatus = async (ticketId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Update ticket priority
export const updateTicketPriority = async (ticketId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}/priority`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Reopen ticket
export const reopenSupportTicket = async (ticketId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}/reopen`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

// Delete ticket (soft delete)
export const deleteSupportTicket = async (ticketId, data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}/delete`, {
    method: 'DELETE',
    body: JSON.stringify(data)
  });
};

// Get support statistics - returns { stats: {}, categories: [], admins: [], recent_tickets: [] }
export const getSupportStats = async () => {
  return apiCall(`${BASE_URL}/admin/support/stats`);
};

// Manage categories (CRUD operations)
export const getSupportCategories = async () => {
  return apiCall(`${BASE_URL}/admin/support/categories`);
};

export const createSupportCategory = async (data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/categories`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const updateSupportCategory = async (data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/categories`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
};

export const deleteSupportCategory = async (data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/categories`, {
    method: 'DELETE',
    body: JSON.stringify(data)
  });
};

// Bulk update tickets
export const bulkUpdateTickets = async (data = {}) => {
  return apiCall(`${BASE_URL}/admin/support/bulk-update`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

// Get ticket attachments
export const getTicketAttachments = async (ticketId) => {
  return apiCall(`${BASE_URL}/admin/support/tickets/${ticketId}/attachments`);
};

// ==================== REPORT MANAGEMENT API ====================

// Listing Reports
export const getReportedListings = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/reports/listings?${queryString}`);
};

export const getListingReportDetail = async (reportId) => {
  return apiCall(`${BASE_URL}/admin/reports/listings/${reportId}`);
};

export const handleListingReport = async (reportId, action, adminNotes = '', severity = 'medium', sendEmail = true, sellerUserId) => {
  return apiCall(`${BASE_URL}/admin/reports/listings/${reportId}/handle`, {
    method: 'PUT',
    body: JSON.stringify({ 
      action, 
      admin_notes: adminNotes, 
      severity, 
      send_email: sendEmail,
      seller_id: sellerUserId 
    })
  });
};

export const bulkHandleListingReports = async (reportIds, action, adminNotes = 'Bulk action') => {
  return apiCall(`${BASE_URL}/admin/reports/listings/bulk-handle`, {
    method: 'POST',
    body: JSON.stringify({ 
      report_ids: reportIds, 
      action, 
      admin_notes: adminNotes 
    })
  });
};

// Seller Reports
export const getReportedSellers = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return apiCall(`${BASE_URL}/admin/reports/sellers?${queryString}`);
};

export const getSellerReportDetail = async (reportId) => {
  return apiCall(`${BASE_URL}/admin/reports/sellers/${reportId}`);
};

export const handleSellerReport = async (reportId, action, adminNotes = '', severity = 'medium', sendEmail = true) => {
  return apiCall(`${BASE_URL}/admin/reports/sellers/${reportId}/handle`, {
    method: 'PUT',
    body: JSON.stringify({ 
      action, 
      admin_notes: adminNotes, 
      severity, 
      send_email: sendEmail 
    })
  });
};

export const bulkHandleSellerReports = async (reportIds, action, adminNotes = 'Bulk action') => {
  return apiCall(`${BASE_URL}/admin/reports/sellers/bulk-handle`, {
    method: 'POST',
    body: JSON.stringify({ 
      report_ids: reportIds, 
      action, 
      admin_notes: adminNotes 
    })
  });
};

// ==================== UTILITY FUNCTIONS ====================
export const logout = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminData');
  window.location.href = '/login';
};

export const getCurrentAdmin = () => {
  const adminData = localStorage.getItem('adminData');
  return adminData ? JSON.parse(adminData) : null;
};

export const isAuthenticated = () => {
  const token = localStorage.getItem('adminToken');
  return token && !isTokenExpired(token);
};